import type { PiniaPlugin, PiniaPluginContext } from 'pinia';
import type { ComponentInternalInstance} from 'vue';
import { computed, reactive } from 'vue';
import isPromise from 'is-promise';

declare module 'pinia' {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface DefineStoreOptionsBase<S, Store> {
        onActivated?: (this: Store) => void;
        onDeactivated?: (this: Store) => void;
    }
}

export const piniaHooksPlugin: PiniaPlugin = ({
    store,
    options: storeOptions,
}: PiniaPluginContext) => {
    // might not need this check, but not sure this plugin code is guaranteed to only be called once
    if (store._trackedStoreUsers != null) return;

    // keep a list of all components using this store
    store._trackedStoreUsers = reactive<Record<string, boolean>>({});
    store._trackedStoreUsersCount = computed(
        () => Object.keys(store._trackedStoreUsers).length
    );
    // expose this info to devtools
    // TODO: determine the best way to safely check in both vite and webpack setups
    if (import.meta.env.DEV || process.env.NODE_ENV === 'development') {
        store._customProperties.add('_trackedStoreUsers');
        store._customProperties.add('_trackedStoreUsersCount');
    }

    function trackStoreUse(
        component: ComponentInternalInstance,
        trackedComponentId: string
    ): void {
        // bail if already tracked - which can happen when stores are using each other in getters
        if (store._trackedStoreUsers[trackedComponentId] != null) return;

        store._trackedStoreUsers[trackedComponentId] = true;
        if (store._trackedStoreUsersCount === 1 && storeOptions.onActivated != null) {
            // console.info('STORE', `${store.$id} - ACTIVATE`);
            // activation fn can return a deactivate / cleanup fn
            store.onDeactivated = storeOptions.onDeactivated;
            storeOptions.onActivated.call(store);

            // activate could be async, so need to resolve if so...
            // TODO may need to think more about this - what if activate errors out?
            if (isPromise(store.onDeactivated)) {
                store.onDeactivated.then(resolvedOnDeactivate => {
                    store.onDeactivated = resolvedOnDeactivate;
                });
            }
        }

        // attach the the unmounted hook here so it only ever gets added once
        // (because we bailed above if this component was already tracked)
        const componentAny = component as any;

        componentAny.bum ??= [];
        componentAny.bum.push(() => {
            if (store._trackedStoreUsers[trackedComponentId] == null) {
                throw new Error(
                    `[${store.$id}] Expected to find component ${trackedComponentId} in list of users`
                );
            }
            delete store._trackedStoreUsers[trackedComponentId];
            if (store._trackedStoreUsersCount === 0 && storeOptions.onDeactivated != null) {
                // console.info('STORE', `${store.$id} - DEACTIVATE`);
                storeOptions.onDeactivated.call(store);
            }
        });
    }

    store.trackStoreUsedByComponent = (component: ComponentInternalInstance): void => {
        // calling lifecycle hooks here (ie beforeMount) causes problems for useStore calls within other store getters/actions
        // so we're injecting the hooks directly into the vue component instance
        // this is probably inadvisable... but seems to work and I don't believe this will change any time soon

        // as an added bonus, this means `watch` calls in our onActivated hook are not bound to the first component that used this store
        // so they will not be destroyed on unmount. This requires us to clean up after ourselves, but it is the desired behaviour.

        const componentIdForUseTracking = `${component.type.__name}/${component.uid}`;
        const componentAny = component as any;

        if (component.isMounted) {
            trackStoreUse(component, componentIdForUseTracking);
        } else {
            // note - this can happen multiple times, but we handle this case in `trackStoreUse()`
            componentAny.m ??= [];
            componentAny.m.push(() => {
                trackStoreUse(component, componentIdForUseTracking);
            });
        }
    };
};
